import {useAuthenticateUser} from '@/providers/JWTProvider';
import {errorMap} from '@/utils/zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Button, Container, Stack} from '@mui/material';
import {RhfTextField} from 'mui-rhf-integration';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {useNavigate, useParams} from "react-router-dom";
import type {JWTUser} from "@/types/JWTUser";

type Props = {
    setShowInvalidCredentialsAlert: (showAlert: boolean) => void;
};

const studentLoginFormSchema = z.object({
    username: z.string(),
    password: z.string(),
});

export type StudentLoginFormSchemaType = z.infer<typeof studentLoginFormSchema>;

const SignInForm = ({setShowInvalidCredentialsAlert}: Props): React.ReactElement => {
    const {actionItem, activityId} = useParams();
    const navigate = useNavigate();
    const authenticateUser = useAuthenticateUser();

    const form = useForm<StudentLoginFormSchemaType>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            username: '',
            password: '',
        },
        resolver: zodResolver(studentLoginFormSchema, {errorMap}),
    });

    const handleSubmit = async () => {
        const formValues = form.getValues();
        setShowInvalidCredentialsAlert(false);

        let user: JWTUser | null = await authenticateUser(formValues.username, formValues.password);

        if (import.meta.env.REACT_APP_ENABLE_JWT_OVERRIDE && import.meta.env.REACT_APP_ADMIN_JWT) {
            user = {
                accessToken: import.meta.env.REACT_APP_ADMIN_JWT as string,
                displayName: '',
            };
            localStorage.setItem('JWTUser', JSON.stringify(user));
        }

        if (user) {
            window.location.reload();
        } else {
            setShowInvalidCredentialsAlert(true);
        }
    };

    return (
        <Container maxWidth='sm' sx={{mt: 3, backgroundColor: '#fff', borderRadius: '4px', p: 3}}>
            <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                <Stack>
                    <RhfTextField
                        control={form.control}
                        name='username'
                        label='Username'
                        placeholder='Username'
                        sx={{my: 1}}
                        onChangeCapture={() => {
                            form.clearErrors();
                        }}
                    />
                    <RhfTextField
                        type='password'
                        control={form.control}
                        name='password'
                        label='Password'
                        placeholder='Password'
                        sx={{my: 1}}
                        onChangeCapture={() => {
                            form.clearErrors();
                        }}
                    />
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: (actionItem === 'interested' ? 'space-between' : 'flex-end'),
                        }}
                    >
                        {actionItem === 'interested' && <Button
                            onClick={() => {
                                navigate(`/qr/interested-form/${activityId}`);
                            }}
                            variant='outlined'
                            type='button'
                            sx={{
                                color: 'gray',
                                borderColor: 'gray',
                                '&:hover': {
                                    backgroundColor: 'lightgray',
                                },
                            }}
                        >
                            Incoming Student
                        </Button>}
                        <Button variant='outlined' type='submit' color='primary'>
                            Submit
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </Container>
    );
};

export default SignInForm;
